import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '../../../components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Switch } from '../../../components/ui/switch';
import { Tooltip } from '../../../components/ui/tooltip';
import { ArrowLeft, HelpCircle, Trash2 } from 'lucide-react';
import {
  convertLengthFromFt,
  convertLengthToFt,
  convertLengthFromMm,
  convertLengthToMm,
} from '../../../utils/unitUtils';
import pipefitterApi from '../../../services/pipefitterApi';
import type { SequenceTableRow } from '../types';
import type { PipeSpecificationForSequence } from './GenerateSequenceModal';

/** Prefill response from GET /v1/sequence/prefill */
interface PrefillResponse {
  id?: number;
  projectNumber?: string;
  sequenceName?: string;
  spreadId?: number;
  subSpreadId?: number | null;
  nominalPipeLengthFt?: number;
  targetNumberOfPipes?: number;
  targetPipelineLengthFt?: number;
  nominalODmm?: number;
  exclusion6OClockMm?: number;
  weldSeamExclusionMm?: number;
  hiLoThresholdMm?: number;
  pipeMaterial?: string;
  pipeType?: string;
  pipeRotation?: string;
  fitQuality?: string;
  allowPipeTranslation?: boolean;
  pipeIds?: string[];
}

function mapPipeTypeFromApi(value: string | undefined): 'single' | 'double' {
  if (!value) return 'single';
  const v = value.toLowerCase();
  if (v === 'double' || v === 'dsaw') return 'double';
  return 'single';
}

function mapPipeRotationFromApi(value: string | undefined): 'any' | 'clockwise' | 'antiClockwise' {
  if (!value) return 'any';
  const v = value.toLowerCase();
  if (v.includes('clock') && !v.includes('anti')) return 'clockwise';
  if (v.includes('anti') || v.includes('counter')) return 'antiClockwise';
  return 'any';
}

function mapFitQualityFromApi(value: string | undefined): 'optimal' | 'acceptable' {
  if (!value) return 'optimal';
  const v = value.toLowerCase();
  if (v === 'acceptable') return 'acceptable';
  return 'optimal';
}

export const SEQUENCE_SETTINGS_MODAL_WIDTH = 'max-w-3xl';

const LENGTH_UNITS = ['ft', 'm'] as const;
const MM_UNITS = ['mm', 'in'] as const;
type LengthUnit = (typeof LENGTH_UNITS)[number];
type MmUnit = (typeof MM_UNITS)[number];

const LENGTH_UNIT_LABELS: Record<LengthUnit, string> = { ft: 'feet', m: 'meter' };
const MM_UNIT_LABELS: Record<MmUnit, string> = { mm: 'millimeters', in: 'inches' };

const FIELD_TOOLTIPS: Record<string, string> = {
  nominalPipeLengthFt: 'Nominal length of the pipe in feet.',
  targetNumberOfPipes: 'The expected number of pipes to be used in this project.',
  targetPipelineLengthFt:
    'The expected length of the pipeline. This parameter is automatically calculated based on the nominal pipe length and the target number of pipes.',
  nominalODMm: 'The nominal outer diameter of the pipe in millimeters.',
  exclusion6OClockDeg:
    'Angular exclusion at the six o\'clock position, in degrees.',
  weldSeamExclusionDeg:
    'Angular exclusion around the weld seam, in degrees.',
  hiloThresholdMm:
    'The maximum radial difference of two pipes (in millimeters).',
  pipeMaterial: 'Material of the pipe.',
  pipeType: 'The type of pipe used for the build process.',
  pipeRotation:
    'The direction in which the pipe may be rotated while building the pipeline.',
  fitQuality:
    'Select alignment mode: find the best rotation (optimal) or use the first rotation that works (acceptable).',
  allowPipeTranslation:
    'Allow reversing the pipe direction (A → B or B → A) during pipeline creation.',
};

/** Selected sequence or sub-sequence from the left list (we call it "sequence" in UI). */
export interface SequenceConfiguration {
  id: number;
  spreadName?: string;
  subSpreadName?: string;
  targetNumberOfPipes?: number;
  nominalPipeLengthFt?: number;
  targetPipelineLengthFt?: number;
  nominalODMm?: number;
  exclusionSixOClockDeg?: number;
  weldSeamExclusionDeg?: number;
  hiloThresholdMm?: number;
  [key: string]: unknown;
}

interface SequenceSettingsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  row?: SequenceTableRow | null;
  /** When provided, modal is in "sequence" mode: prefill from this config and show Delete. */
  configuration?: SequenceConfiguration | null;
  selectionType?: 'Spread' | 'SubSpread';
  /** For prefill API and name-only update: spread id (when editing a spread). */
  spreadId?: number | string;
  /** For prefill API and name-only update: project number. */
  projectNumber?: string;
  /** For prefill API when editing a sub-spread: sub-spread id; parent spread id should be passed as spreadId. */
  subSpreadId?: number | string;
  onSave?: (row: SequenceTableRow, settings: Record<string, unknown>) => void | Promise<void>;
  /** Called when saving in configuration mode (sequence/sub-sequence edit). Payload includes spreadName/subSpreadName and config. */
  onSaveConfiguration?: (settings: Record<string, unknown>) => void | Promise<void>;
  /** When only the spread name changed (Spread mode): call this instead of regenerate. */
  onSaveNameOnly?: (params: { spreadId: number | string; projectNumber: string; sequenceName: string }) => void | Promise<void>;
  /** When any configuration changed (or SubSpread): call with full settings for pipe-based-generate (mode: Regenerate). */
  onSaveRegenerate?: (settings: Record<string, unknown>) => void | Promise<void>;
  onDeleteSequence?: () => void;
  loading?: boolean;
  /** Align nominal length / OD (mm) with project pipe size selector. */
  pipeSpecification?: PipeSpecificationForSequence | null;
  /** When set, sync target pipe count (and pipeline length) to pipes currently in sequence. */
  sequencePipeCount?: number;
}

type SequenceSettingsNumericKey =
  | 'nominalPipeLengthFt'
  | 'targetNumberOfPipes'
  | 'targetPipelineLengthFt'
  | 'nominalODMm'
  | 'exclusion6OClockDeg'
  | 'weldSeamExclusionDeg'
  | 'hiloThresholdMm';

interface SequenceSettingsFormState {
  sequenceName: string;
  nominalPipeLengthFt: string;
  targetNumberOfPipes: string;
  targetPipelineLengthFt: string;
  nominalODMm: string;
  exclusion6OClockDeg: string;
  weldSeamExclusionDeg: string;
  hiloThresholdMm: string;
  pipeMaterial: string;
  pipeType: 'single' | 'double';
  pipeRotation: 'any' | 'clockwise' | 'antiClockwise';
  fitQuality: 'optimal' | 'acceptable';
  allowPipeTranslation: boolean;
}

type SequenceSettingsErrors = Partial<Record<SequenceSettingsNumericKey, string>> & { sequenceName?: string };

const DEFAULT_SEQUENCE_SETTINGS: SequenceSettingsFormState = {
  sequenceName: '',
  nominalPipeLengthFt: '40',
  targetNumberOfPipes: '24',
  targetPipelineLengthFt: '960',
  nominalODMm: '208',
  exclusion6OClockDeg: '36',
  weldSeamExclusionDeg: '18',
  hiloThresholdMm: '1.5',
  pipeMaterial: 'Iron',
  pipeType: 'single',
  pipeRotation: 'any',
  fitQuality: 'optimal',
  allowPipeTranslation: false,
};

const CONFIG_KEYS: (keyof SequenceSettingsFormState)[] = [
  'nominalPipeLengthFt',
  'targetNumberOfPipes',
  'targetPipelineLengthFt',
  'nominalODMm',
  'exclusion6OClockDeg',
  'weldSeamExclusionDeg',
  'hiloThresholdMm',
  'pipeMaterial',
  'pipeType',
  'pipeRotation',
  'fitQuality',
  'allowPipeTranslation',
];

export const SequenceSettingsModal: React.FC<SequenceSettingsModalProps> = ({
  open,
  onOpenChange,
  row = null,
  configuration = null,
  selectionType,
  spreadId,
  projectNumber,
  subSpreadId,
  onSave,
  onSaveConfiguration,
  onSaveNameOnly,
  onSaveRegenerate,
  onDeleteSequence,
  loading = false,
  pipeSpecification = null,
  sequencePipeCount,
}) => {
  const [formState, setFormState] = useState<SequenceSettingsFormState>(DEFAULT_SEQUENCE_SETTINGS);
  const [errors, setErrors] = useState<SequenceSettingsErrors>({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [prefillLoading, setPrefillLoading] = useState(false);
  const [prefillError, setPrefillError] = useState<string | null>(null);
  const initialFormSnapshotRef = useRef<SequenceSettingsFormState | null>(null);

  const [nominalPipeLengthUnit, setNominalPipeLengthUnit] = useState<LengthUnit>('ft');
  const [targetPipelineLengthUnit, setTargetPipelineLengthUnit] = useState<LengthUnit>('ft');
  const [nominalODUnit, setNominalODUnit] = useState<MmUnit>('mm');
  const [hiloThresholdUnit, setHiloThresholdUnit] = useState<MmUnit>('mm');

  const isConfigurationMode = open && !!configuration;
  const isRowMode = open && !!row && !configuration;

  // Prefill from API when spreadId and projectNumber are provided (configuration mode)
  useEffect(() => {
    if (!open || !isConfigurationMode || !spreadId || !projectNumber) {
      if (!open) {
        initialFormSnapshotRef.current = null;
        setPrefillError(null);
      }
      return;
    }
    let cancelled = false;
    setPrefillError(null);
    setPrefillLoading(true);
    const params = new URLSearchParams({ spreadId: String(spreadId), projectNumber });
    if (subSpreadId != null && subSpreadId !== '') {
      params.set('subSpreadId', String(subSpreadId));
    }
    pipefitterApi
      .get(`/sequence/prefill?${params.toString()}`)
      .then((res: { data?: { status?: number; data?: { prefill?: PrefillResponse | null; spreadName?: string; subSpreadName?: string | null } } }) => {
        if (cancelled) return;
        const data = res?.data?.data;
        const prefill = data?.prefill ?? null;
        const spreadNameFromApi = data?.spreadName ?? '';
        const subSpreadNameFromApi = data?.subSpreadName ?? '';
        const sequenceName =
          selectionType === 'SubSpread'
            ? (prefill?.sequenceName ?? subSpreadNameFromApi ?? '')
            : (prefill?.sequenceName ?? spreadNameFromApi ?? '');
        const nextState: SequenceSettingsFormState = {
          sequenceName,
          nominalPipeLengthFt: String(prefill?.nominalPipeLengthFt ?? DEFAULT_SEQUENCE_SETTINGS.nominalPipeLengthFt),
          targetNumberOfPipes: String(prefill?.targetNumberOfPipes ?? DEFAULT_SEQUENCE_SETTINGS.targetNumberOfPipes),
          targetPipelineLengthFt: String(prefill?.targetPipelineLengthFt ?? DEFAULT_SEQUENCE_SETTINGS.targetPipelineLengthFt),
          nominalODMm: String(prefill?.nominalODmm ?? DEFAULT_SEQUENCE_SETTINGS.nominalODMm),
          exclusion6OClockDeg:
            prefill?.exclusion6OClockMm != null
              ? String(prefill.exclusion6OClockMm)
              : DEFAULT_SEQUENCE_SETTINGS.exclusion6OClockDeg,
          weldSeamExclusionDeg:
            prefill?.weldSeamExclusionMm != null
              ? String(prefill.weldSeamExclusionMm)
              : DEFAULT_SEQUENCE_SETTINGS.weldSeamExclusionDeg,
          hiloThresholdMm: String(prefill?.hiLoThresholdMm ?? DEFAULT_SEQUENCE_SETTINGS.hiloThresholdMm),
          pipeMaterial: prefill?.pipeMaterial ?? DEFAULT_SEQUENCE_SETTINGS.pipeMaterial,
          pipeType: mapPipeTypeFromApi(prefill?.pipeType),
          pipeRotation: mapPipeRotationFromApi(prefill?.pipeRotation),
          fitQuality: mapFitQualityFromApi(prefill?.fitQuality),
          allowPipeTranslation: prefill?.allowPipeTranslation ?? DEFAULT_SEQUENCE_SETTINGS.allowPipeTranslation,
        };
        setFormState(nextState);
        initialFormSnapshotRef.current = nextState;
        setNominalPipeLengthUnit('ft');
        setTargetPipelineLengthUnit('ft');
        setNominalODUnit('mm');
        setHiloThresholdUnit('mm');
      })
      .catch((err: { message?: string; response?: { data?: { message?: string } } }) => {
        if (cancelled) return;
        const msg =
          typeof err?.response?.data === 'string'
            ? err.response.data
            : err?.response?.data?.message ?? err?.message ?? 'Failed to load sequence settings';
        setPrefillError(msg);
        setFormState(DEFAULT_SEQUENCE_SETTINGS);
        initialFormSnapshotRef.current = null;
      })
      .finally(() => {
        if (!cancelled) setPrefillLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, isConfigurationMode, spreadId, projectNumber, subSpreadId, selectionType]);

  // Fallback: prefill from configuration prop when API prefill is not used (no spreadId/projectNumber)
  useEffect(() => {
    if (!open || !configuration || (spreadId != null && projectNumber != null)) return;
    const sequenceName =
      selectionType === 'SubSpread'
        ? (configuration.subSpreadName ?? '')
        : (configuration.spreadName ?? '');
    const nextState: SequenceSettingsFormState = {
      ...DEFAULT_SEQUENCE_SETTINGS,
      sequenceName,
      targetNumberOfPipes: String(configuration.targetNumberOfPipes ?? DEFAULT_SEQUENCE_SETTINGS.targetNumberOfPipes),
      nominalPipeLengthFt: String(configuration.nominalPipeLengthFt ?? DEFAULT_SEQUENCE_SETTINGS.nominalPipeLengthFt),
      targetPipelineLengthFt: String(configuration.targetPipelineLengthFt ?? DEFAULT_SEQUENCE_SETTINGS.targetPipelineLengthFt),
      nominalODMm: String(configuration.nominalODMm ?? DEFAULT_SEQUENCE_SETTINGS.nominalODMm),
      exclusion6OClockDeg: String(configuration.exclusionSixOClockDeg ?? DEFAULT_SEQUENCE_SETTINGS.exclusion6OClockDeg),
      weldSeamExclusionDeg: String(configuration.weldSeamExclusionDeg ?? DEFAULT_SEQUENCE_SETTINGS.weldSeamExclusionDeg),
      hiloThresholdMm: String(configuration.hiloThresholdMm ?? DEFAULT_SEQUENCE_SETTINGS.hiloThresholdMm),
    };
    setFormState(nextState);
    initialFormSnapshotRef.current = nextState;
    setNominalPipeLengthUnit('ft');
    setTargetPipelineLengthUnit('ft');
    setNominalODUnit('mm');
    setHiloThresholdUnit('mm');
  }, [open, configuration?.id, selectionType, spreadId, projectNumber]);

  const pipeSpecKey = useMemo(
    () => (pipeSpecification ? JSON.stringify(pipeSpecification) : ''),
    [pipeSpecification]
  );

  useEffect(() => {
    if (!open || !isConfigurationMode || prefillLoading || !pipeSpecification) return;
    setNominalPipeLengthUnit('ft');
    setTargetPipelineLengthUnit('ft');
    setNominalODUnit('mm');
    setFormState((prev) => {
      const lenFt = pipeSpecification.nominalPipeLengthFt;
      const pipes = Math.max(Number(prev.targetNumberOfPipes) || 1, 1);
      const pipelineFt = pipes * lenFt;
      return {
        ...prev,
        nominalPipeLengthFt: String(lenFt),
        nominalODMm: String(pipeSpecification.nominalODMm),
        targetPipelineLengthFt: String(pipelineFt),
      };
    });
  }, [open, isConfigurationMode, prefillLoading, pipeSpecKey, pipeSpecification]);

  useEffect(() => {
    if (!open || !isConfigurationMode || prefillLoading) return;
    if (sequencePipeCount == null || sequencePipeCount < 1) return;
    setFormState((prev) => {
      const pipes = sequencePipeCount;
      const lengthDisplay = Number(prev.nominalPipeLengthFt);
      if (Number.isNaN(lengthDisplay) || lengthDisplay <= 0) return { ...prev, targetNumberOfPipes: String(pipes) };
      const lenFt = convertLengthToFt(lengthDisplay, nominalPipeLengthUnit);
      const pipelineFt = pipes * lenFt;
      return {
        ...prev,
        targetNumberOfPipes: String(pipes),
        targetPipelineLengthFt: String(convertLengthFromFt(pipelineFt, targetPipelineLengthUnit)),
      };
    });
  }, [
    open,
    isConfigurationMode,
    prefillLoading,
    sequencePipeCount,
    nominalPipeLengthUnit,
    targetPipelineLengthUnit,
  ]);

  const metricsPills = useMemo(() => {
    const nPipes = formState.targetNumberOfPipes;
    return [
      {
        label: 'Nominal Pipe Length',
        text: `${formState.nominalPipeLengthFt} ${LENGTH_UNIT_LABELS[nominalPipeLengthUnit]}`,
        bgColor: 'bg-violet-100 text-violet-800',
      },
      {
        label: 'Target Number of Pipes',
        text: `${nPipes} pipes`,
        bgColor: 'bg-amber-100 text-amber-800',
      },
      {
        label: 'Target Pipeline Length',
        text: `${formState.targetPipelineLengthFt} ${LENGTH_UNIT_LABELS[targetPipelineLengthUnit]}`,
        bgColor: 'bg-emerald-100 text-emerald-800',
      },
      {
        label: 'Nominal OD',
        text: `${formState.nominalODMm} ${MM_UNIT_LABELS[nominalODUnit]}`,
        bgColor: 'bg-sky-100 text-sky-800',
      },
    ];
  }, [
    formState.nominalPipeLengthFt,
    formState.targetNumberOfPipes,
    formState.targetPipelineLengthFt,
    formState.nominalODMm,
    nominalPipeLengthUnit,
    targetPipelineLengthUnit,
    nominalODUnit,
  ]);

  if (!row && !configuration) return null;

  const validateNumericField = (key: SequenceSettingsNumericKey, value: string): string | undefined => {
    if (key === 'targetNumberOfPipes') {
      if (!value || value.trim() === '') return 'Required';
    } else if (!value || value.trim() === '') {
      return undefined;
    }
    const numeric = Number(value);
    if (Number.isNaN(numeric)) return 'Must be a number';
    if (numeric <= 0) return 'Must be a positive number';
    if (
      (key === 'exclusion6OClockDeg' || key === 'weldSeamExclusionDeg') &&
      numeric > 360
    ) {
      return 'Value is too large (max 360 deg)';
    }
    return undefined;
  };

  const handleNumericChange = (key: SequenceSettingsNumericKey, value: string) => {
    setFormState((prev) => {
      const next = { ...prev, [key]: value };
      setErrors((prevErrors) => {
        const cloned = { ...prevErrors };
        const maybe = validateNumericField(key, value);
        if (maybe) cloned[key] = maybe;
        else delete cloned[key];
        return cloned;
      });

      if (key === 'targetNumberOfPipes' || key === 'nominalPipeLengthFt') {
        const pipes = Number(next.targetNumberOfPipes);
        const lengthDisplay = Number(next.nominalPipeLengthFt);
        if (!Number.isNaN(pipes) && !Number.isNaN(lengthDisplay) && pipes > 0 && lengthDisplay > 0) {
          const lengthFt = convertLengthToFt(lengthDisplay, nominalPipeLengthUnit);
          const pipelineLengthFt = pipes * lengthFt;
          next.targetPipelineLengthFt = convertLengthFromFt(pipelineLengthFt, targetPipelineLengthUnit).toString();
          const tlError = validateNumericField('targetPipelineLengthFt', next.targetPipelineLengthFt);
          setErrors((prevErrors) => {
            const cloned = { ...prevErrors };
            if (tlError) cloned.targetPipelineLengthFt = tlError;
            else delete cloned.targetPipelineLengthFt;
            return cloned;
          });
        }
      }

      return next;
    });
  };

  const handleLengthUnitChange = (
    field: 'nominalPipeLengthFt' | 'targetPipelineLengthFt',
    newUnit: LengthUnit
  ) => {
    const setUnit = field === 'nominalPipeLengthFt' ? setNominalPipeLengthUnit : setTargetPipelineLengthUnit;
    const currentUnit = field === 'nominalPipeLengthFt' ? nominalPipeLengthUnit : targetPipelineLengthUnit;
    setUnit(newUnit);
    setFormState((prev) => {
      const str = String(prev[field] ?? '').trim();
      if (str === '') return prev;
      const raw = Number(str);
      if (Number.isNaN(raw)) return prev;
      const ft = convertLengthToFt(raw, currentUnit);
      const display = convertLengthFromFt(ft, newUnit);
      return { ...prev, [field]: display.toString() };
    });
  };

  const handleMmUnitChange = (
    field: 'nominalODMm' | 'hiloThresholdMm',
    newUnit: MmUnit
  ) => {
    const setUnit = field === 'nominalODMm' ? setNominalODUnit : setHiloThresholdUnit;
    const currentUnit = field === 'nominalODMm' ? nominalODUnit : hiloThresholdUnit;
    setUnit(newUnit);
    setFormState((prev) => {
      const str = String(prev[field] ?? '').trim();
      if (str === '') return prev;
      const raw = Number(str);
      if (Number.isNaN(raw)) return prev;
      const mm = convertLengthToMm(raw, currentUnit);
      const display = convertLengthFromMm(mm, newUnit);
      return { ...prev, [field]: display.toString() };
    });
  };

  const handleCancel = () => {
    if (!loading) {
      onOpenChange(false);
    }
  };

  const handleUpdate = async () => {
    const numericKeys: SequenceSettingsNumericKey[] = [
      'nominalPipeLengthFt',
      'targetNumberOfPipes',
      'targetPipelineLengthFt',
      'nominalODMm',
      'exclusion6OClockDeg',
      'weldSeamExclusionDeg',
      'hiloThresholdMm',
    ];

    const validation: SequenceSettingsErrors = {};
    numericKeys.forEach((key) => {
      const maybe = validateNumericField(key, formState[key]);
      if (maybe) validation[key] = maybe;
    });

    if (isConfigurationMode && !formState.sequenceName.trim()) {
      setErrors((prev) => ({ ...prev, sequenceName: 'Sequence name is required' }));
      return;
    }
    setErrors(validation);
    if (Object.keys(validation).length > 0) return;

    const nominalODMmCanonical = convertLengthToMm(Number(formState.nominalODMm), nominalODUnit);

    const nominalPipeLengthFtCanonical = convertLengthToFt(Number(formState.nominalPipeLengthFt), nominalPipeLengthUnit);
    const targetPipelineLengthFtCanonical = convertLengthToFt(Number(formState.targetPipelineLengthFt), targetPipelineLengthUnit);
    const hiloThresholdMmCanonical = convertLengthToMm(Number(formState.hiloThresholdMm), hiloThresholdUnit);

    const payload: Record<string, unknown> = {
      nominalPipeLengthFt: nominalPipeLengthFtCanonical,
      targetNumberOfPipes: Number(formState.targetNumberOfPipes),
      targetPipelineLengthFt: targetPipelineLengthFtCanonical,
      nominalODMm: nominalODMmCanonical,
      exclusion6OClockMm: Number(formState.exclusion6OClockDeg),
      weldSeamExclusionMm: Number(formState.weldSeamExclusionDeg),
      hiloThresholdMm: hiloThresholdMmCanonical,
      pipeMaterial: formState.pipeMaterial,
      pipeType: formState.pipeType,
      pipeRotation: formState.pipeRotation,
      fitQuality: formState.fitQuality,
      allowPipeTranslation: formState.allowPipeTranslation,
    };

    if (isConfigurationMode) {
      const nameTrimmed = formState.sequenceName.trim();
      if (selectionType === 'SubSpread') {
        payload.subSpreadName = nameTrimmed || undefined;
      } else {
        payload.spreadName = nameTrimmed || undefined;
      }
      const initialSnapshot = initialFormSnapshotRef.current;
      const onlyNameChanged =
        selectionType === 'Spread' &&
        initialSnapshot &&
        nameTrimmed !== (initialSnapshot.sequenceName ?? '').trim() &&
        CONFIG_KEYS.every((key) => {
          const a = formState[key];
          const b = initialSnapshot[key];
          if (typeof a === 'boolean' && typeof b === 'boolean') return a === b;
          return String(a ?? '') === String(b ?? '');
        });
      if (onlyNameChanged && onSaveNameOnly && spreadId != null && projectNumber) {
        onSaveNameOnly({ spreadId, projectNumber, sequenceName: nameTrimmed });
        return;
      }
      if (onSaveRegenerate) {
        payload.mode = 'REGENERATE';
        onSaveRegenerate(payload);
        return;
      }
      if (onSaveConfiguration) {
        await onSaveConfiguration(payload);
      }
    } else if (onSave && row) {
      await onSave(row, payload);
    }
    onOpenChange(false);
  };

  const hasErrors = Object.keys(errors).length > 0;
  const isPrefillBlocked = prefillLoading || !!prefillError;

  const sequenceLabel = configuration
    ? (selectionType === 'SubSpread' ? configuration.subSpreadName : configuration.spreadName) || `Sequence ${configuration.id}`
    : (row && (row.sequenceLabel || (row.key != null ? `Sequence ${row.key}` : row.pipeId || ''))) || '';

  const handleConfirmDelete = () => {
    onDeleteSequence?.();
    setShowDeleteConfirm(false);
    onOpenChange(false);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="w-screen h-screen max-w-none max-h-[100dvh] m-0 p-0 gap-0 border-0 bg-white shadow-none rounded-none [&>button]:hidden flex flex-col">
          <div className="flex flex-col h-full min-h-0 overflow-hidden flex-1">
            <DialogHeader className="px-4 sm:px-6 py-3 border-b flex flex-row items-start gap-3 bg-white shrink-0">
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                disabled={loading}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors shrink-0"
                aria-label="Back"
              >
                <ArrowLeft className="h-5 w-5 text-gray-500" />
              </Button>
              <div className="min-w-0">
                <DialogTitle className="text-lg font-bold text-gray-900">
                  {sequenceLabel ? `Sequence Settings – ${sequenceLabel}` : 'Sequence Settings'}
                </DialogTitle>
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  {metricsPills.map((item) => (
                    <span key={item.label} className={`${item.bgColor} px-2 py-1 rounded text-xs font-medium`}>
                      {item.label}: {item.text}
                    </span>
                  ))}
                </div>
              </div>
            </DialogHeader>

            <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-4 sm:px-6 pb-3">
            {prefillLoading && (
              <div className="py-8 text-center text-sm text-gray-600">
                Loading sequence settings…
              </div>
            )}
            {prefillError && (
              <div className="py-4 px-4 mb-4 rounded-md bg-red-50 border border-red-200">
                <p className="text-sm text-red-700">{prefillError}</p>
              </div>
            )}
            <div className="pt-2 pb-3">
              {configuration ? (
                <p className="text-sm text-gray-600">
                  Update configuration for sequence: <span className="font-medium text-gray-700">{sequenceLabel}</span>
                </p>
              ) : row ? (
                <p className="text-sm text-gray-600">
                  Update configuration for Pipe ID: <span className="font-medium text-gray-700">{row.pipeId}</span>
                </p>
              ) : null}
            </div>

            {isConfigurationMode && (
              <div className="mb-6 max-w-md space-y-1.5">
                <Label className="text-sm font-medium text-gray-700">
                  Sequence Name
                </Label>
                <Input
                  value={formState.sequenceName}
                  onChange={(e) => {
                    setFormState((prev) => ({ ...prev, sequenceName: e.target.value }));
                    if (errors.sequenceName) setErrors((prev) => ({ ...prev, sequenceName: undefined }));
                  }}
                  disabled={loading || prefillLoading}
                  placeholder="e.g. Run_2026_03_06"
                />
                {errors.sequenceName && (
                  <p className="text-xs text-red-600 mt-1">{errors.sequenceName}</p>
                )}
              </div>
            )}

            <div className="border-t border-gray-200 my-4" />

            <section className="space-y-4 mb-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-x-4 gap-y-4">
                {/*
                  Shown in header pills only; formState still used on save.
                */}
                {(errors.nominalPipeLengthFt ||
                  errors.targetNumberOfPipes ||
                  errors.targetPipelineLengthFt ||
                  errors.nominalODMm) && (
                  <div className="col-span-full space-y-0.5 text-xs text-red-600">
                    {errors.nominalPipeLengthFt && <p>{errors.nominalPipeLengthFt}</p>}
                    {errors.targetNumberOfPipes && <p>{errors.targetNumberOfPipes}</p>}
                    {errors.targetPipelineLengthFt && <p>{errors.targetPipelineLengthFt}</p>}
                    {errors.nominalODMm && <p>{errors.nominalODMm}</p>}
                  </div>
                )}

                <div className="space-y-1.5">
                  <div className="flex items-center gap-1.5">
                    <Label className="text-sm font-medium text-gray-700">
                      6 O&apos;Clock Exclusion (deg)
                    </Label>
                    <Tooltip content={FIELD_TOOLTIPS.exclusion6OClockDeg}>
                      <HelpCircle className="h-4 w-4 text-gray-400 cursor-help shrink-0" />
                    </Tooltip>
                  </div>
                  <Input
                    type="number"
                    value={formState.exclusion6OClockDeg}
                    onChange={(e) => handleNumericChange('exclusion6OClockDeg', e.target.value)}
                    disabled={loading}
                    className="disabled:opacity-70 disabled:cursor-not-allowed"
                  />
                  {errors.exclusion6OClockDeg && (
                    <p className="text-xs text-red-600 mt-1">{errors.exclusion6OClockDeg}</p>
                  )}
                </div>

                <div className="space-y-1.5">
                  <div className="flex items-center gap-1.5">
                    <Label className="text-sm font-medium text-gray-700">
                      Weld Seam Exclusion (deg)
                    </Label>
                    <Tooltip content={FIELD_TOOLTIPS.weldSeamExclusionDeg}>
                      <HelpCircle className="h-4 w-4 text-gray-400 cursor-help shrink-0" />
                    </Tooltip>
                  </div>
                  <Input
                    type="number"
                    value={formState.weldSeamExclusionDeg}
                    onChange={(e) => handleNumericChange('weldSeamExclusionDeg', e.target.value)}
                    disabled={loading}
                    className="disabled:opacity-70 disabled:cursor-not-allowed"
                  />
                  {errors.weldSeamExclusionDeg && (
                    <p className="text-xs text-red-600 mt-1">{errors.weldSeamExclusionDeg}</p>
                  )}
                </div>

                <div className="space-y-1.5">
                  <div className="flex items-center gap-1.5">
                    <Label className="text-sm font-medium text-gray-700">
                      HiLo Threshold <span className="text-red-500">*</span>
                    </Label>
                    <Tooltip content={FIELD_TOOLTIPS.hiloThresholdMm}>
                      <HelpCircle className="h-4 w-4 text-gray-400 cursor-help shrink-0" />
                    </Tooltip>
                  </div>
                  <div className="flex w-full rounded-md border border-gray-200 overflow-hidden">
                    <Input
                      type="number"
                      step="0.1"
                      value={formState.hiloThresholdMm}
                      onChange={(e) => handleNumericChange('hiloThresholdMm', e.target.value)}
                      disabled={loading}
                      className="flex-[0_0_70%] rounded-r-none border-0 border-r border-gray-200 rounded-l-md focus-visible:ring-inset disabled:opacity-70 disabled:cursor-not-allowed"
                    />
                    <Select
                      value={hiloThresholdUnit}
                      onValueChange={(v: MmUnit) => handleMmUnitChange('hiloThresholdMm', v)}
                      disabled={loading}
                    >
                      <SelectTrigger className="flex-[0_0_30%] h-10 rounded-l-none rounded-r-md border-0 border-l border-gray-200">
                        <SelectValue>{MM_UNIT_LABELS[hiloThresholdUnit]}</SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {MM_UNITS.map((u) => (
                          <SelectItem key={u} value={u}>{MM_UNIT_LABELS[u]}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {errors.hiloThresholdMm && (
                    <p className="text-xs text-red-600 mt-1">{errors.hiloThresholdMm}</p>
                  )}
                </div>

                <div className="space-y-1.5">
                  <div className="flex items-center gap-1.5">
                    <Label className="text-sm font-medium text-gray-700">Pipe Material</Label>
                    <Tooltip content={FIELD_TOOLTIPS.pipeMaterial}>
                      <HelpCircle className="h-4 w-4 text-gray-400 cursor-help shrink-0" />
                    </Tooltip>
                  </div>
                  <Input
                    type="text"
                    value={formState.pipeMaterial}
                    onChange={(e) =>
                      setFormState((prev) => ({ ...prev, pipeMaterial: e.target.value }))
                    }
                    disabled={loading}
                    className="disabled:opacity-70 disabled:cursor-not-allowed"
                  />
                </div>

                <div className="space-y-1.5">
                  <div className="flex items-center gap-1.5">
                    <Label className="text-sm font-medium text-gray-700">Pipe Type</Label>
                    <Tooltip content={FIELD_TOOLTIPS.pipeType}>
                      <HelpCircle className="h-4 w-4 text-gray-400 cursor-help shrink-0" />
                    </Tooltip>
                  </div>
                  <Select
                    value={formState.pipeType}
                    onValueChange={(value: 'single' | 'double') =>
                      setFormState((prev) => ({ ...prev, pipeType: value }))
                    }
                    disabled={loading}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="single">Single</SelectItem>
                      <SelectItem value="double">Double</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <div className="flex items-center gap-1.5">
                    <Label className="text-sm font-medium text-gray-700">Pipe Rotation</Label>
                    <Tooltip content={FIELD_TOOLTIPS.pipeRotation}>
                      <HelpCircle className="h-4 w-4 text-gray-400 cursor-help shrink-0" />
                    </Tooltip>
                  </div>
                  <Select
                    value={formState.pipeRotation}
                    onValueChange={(value: 'any' | 'clockwise' | 'antiClockwise') =>
                      setFormState((prev) => ({ ...prev, pipeRotation: value }))
                    }
                    disabled={loading}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any">Any</SelectItem>
                      <SelectItem value="clockwise">Clockwise</SelectItem>
                      <SelectItem value="antiClockwise">Anti Clockwise</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <div className="flex items-center gap-1.5">
                    <Label className="text-sm font-medium text-gray-700">Fit Quality</Label>
                    <Tooltip content={FIELD_TOOLTIPS.fitQuality}>
                      <HelpCircle className="h-4 w-4 text-gray-400 cursor-help shrink-0" />
                    </Tooltip>
                  </div>
                  <Select
                    value={formState.fitQuality}
                    onValueChange={(value: 'optimal' | 'acceptable') =>
                      setFormState((prev) => ({ ...prev, fitQuality: value }))
                    }
                    disabled={loading}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="optimal">Optimal - Use the best rotation that works</SelectItem>
                      <SelectItem value="acceptable">Acceptable - Use the first rotation that works</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex flex-col gap-1.5 rounded-lg border border-gray-200 px-4 py-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <Label htmlFor="allow-pipe-translation-settings" className="text-sm font-medium text-gray-700">
                    Allow Pipe Translation
                  </Label>
                  <Tooltip content={FIELD_TOOLTIPS.allowPipeTranslation}>
                    <HelpCircle className="h-4 w-4 text-gray-400 cursor-help shrink-0" />
                  </Tooltip>
                  <Switch
                    id="allow-pipe-translation-settings"
                    checked={formState.allowPipeTranslation}
                    onCheckedChange={(checked: boolean) =>
                      setFormState((prev) => ({ ...prev, allowPipeTranslation: checked }))
                    }
                    disabled={loading}
                  />
                </div>
                <p className="text-xs text-gray-500">
                  When enabled, pipes can be translated along the axis to improve fit quality.
                </p>
              </div>
            </section>
          </div>

          <div className="flex items-center justify-end gap-3 px-4 py-3 border-t bg-background shrink-0">
            {/* {isConfigurationMode && onDeleteSequence && (
              <Button
                type="button"
                variant="outline"
                className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200 mr-auto"
                onClick={() => setShowDeleteConfirm(true)}
                disabled={loading}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </Button>
            )} */}
            <div className={isConfigurationMode && onDeleteSequence ? '' : 'flex-1'} />
            <Button variant="outline" onClick={handleCancel} disabled={loading}>
              Cancel
            </Button>
            <Button
              onClick={handleUpdate}
              disabled={loading || hasErrors || isPrefillBlocked}
              className="bg-hero-red text-white hover:bg-hero-red-60 min-w-[120px]"
            >
              {loading ? 'Saving...' : 'Update'}
            </Button>
          </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="max-w-[500px] p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
              <Trash2 className="h-5 w-5 text-red-600" />
            </div>
            <DialogTitle className="text-xl font-semibold">Delete Sequence</DialogTitle>
          </div>
          <div className="mb-6">
            <p className="text-gray-700 mb-3">
              Are you sure you want to <span className="font-semibold text-red-600">delete</span> the sequence
              {sequenceLabel ? <span className="font-semibold text-red-600"> {sequenceLabel}</span> : ''}?
            </p>
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-700">
                <span className="font-semibold">Warning:</span> This action is irreversible. The sequence will be permanently removed from the list.
              </p>
            </div>
          </div>
          <DialogFooter className="gap-3">
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)} disabled={loading} className="min-w-[100px]">
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDelete}
              disabled={loading}
              className="bg-red-600 hover:bg-red-700 text-white min-w-[100px]"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
